import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  ErrorState,
  Field,
  Modal,
  Money,
  PageHeader,
  Select,
  Spinner,
  TextField,
} from "../components/ui";
import { DataTable } from "../components/DataTable";
import { dollarsToCents, formatDate, today } from "../lib/format";
import type {
  Account,
  JournalEntryRow,
  JournalEntryDetail,
} from "../lib/models";

export default function JournalEntries() {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["journal", currentOrg?.id],
    queryFn: () => api.get<JournalEntryRow[]>("/journal-entries"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title="Journal Entries"
        subtitle="Manual double-entry adjustments"
        action={<Button onClick={() => setOpen(true)}>New journal entry</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          onRowClick={(r) => setDetailId(r.id)}
          columns={[
            { key: "date", header: "Date", render: (r) => formatDate(r.txnDate) },
            { key: "num", header: "No.", render: (r) => r.docNumber ?? "—" },
            {
              key: "source",
              header: "Source",
              render: (r) => (
                <span className="capitalize text-gray-600">
                  {r.source.replace("_", " ")}
                </span>
              ),
            },
            { key: "memo", header: "Memo", render: (r) => r.memo ?? "—" },
            {
              key: "adj",
              header: "",
              render: (r) =>
                r.isAdjusting ? (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                    adjusting
                  </span>
                ) : null,
            },
            {
              key: "amount",
              header: "Amount",
              align: "right",
              render: (r) => <Money cents={r.amount} currency={currency} />,
            },
          ]}
        />
      )}

      {detailId && (
        <JournalDetailModal
          id={detailId}
          currency={currency}
          onClose={() => setDetailId(null)}
          onDeleted={() => {
            setDetailId(null);
            qc.invalidateQueries({ queryKey: ["journal"] });
            qc.invalidateQueries({ queryKey: ["accounts"] });
          }}
        />
      )}

      <JournalModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={() => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["journal"] });
        }}
      />
    </div>
  );
}

function JournalDetailModal({
  id,
  currency,
  onClose,
  onDeleted,
}: {
  id: string;
  currency: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["journal", "detail", id],
    queryFn: () => api.get<JournalEntryDetail>(`/journal-entries/${id}`),
  });

  const del = useMutation({
    mutationFn: () => api.del(`/journal-entries/${id}`),
    onSuccess: onDeleted,
  });

  const deletable = data?.source === "manual";

  return (
    <Modal open onClose={onClose} title="Journal entry" wide>
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
            <div>
              <span className="text-gray-400">Date: </span>
              {formatDate(data.txnDate)}
            </div>
            <div>
              <span className="text-gray-400">No.: </span>
              {data.docNumber ?? "—"}
            </div>
            <div>
              <span className="text-gray-400">Source: </span>
              <span className="capitalize">{data.source.replace("_", " ")}</span>
            </div>
          </div>
          {data.memo && <p className="text-sm text-gray-600">{data.memo}</p>}

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-3 py-2">Account</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="px-3 py-2 text-gray-800">
                      {l.accountCode ? `${l.accountCode} ` : ""}
                      {l.accountName ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {l.description ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {l.debit ? <Money cents={l.debit} currency={currency} /> : ""}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {l.credit ? <Money cents={l.credit} currency={currency} /> : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {del.error && <ErrorState error={del.error} />}

          <div className="flex items-center justify-between">
            {deletable ? (
              <Button
                variant="danger"
                disabled={del.isPending}
                onClick={() => {
                  if (confirm("Delete this entry? Account balances will be adjusted."))
                    del.mutate();
                }}
              >
                {del.isPending ? "Deleting…" : "Delete entry"}
              </Button>
            ) : (
              <span className="text-xs text-gray-400">
                Posted from a transaction — void that transaction to reverse it.
              </span>
            )}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

interface JLine {
  accountId: string;
  debit: string;
  credit: string;
  description: string;
}
const blank: JLine = { accountId: "", debit: "", credit: "", description: "" };

function JournalModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const { data: accounts } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: open && !!currentOrg,
  });

  const [txnDate, setTxnDate] = useState(today());
  const [memo, setMemo] = useState("");
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [lines, setLines] = useState<JLine[]>([{ ...blank }, { ...blank }]);

  const totalDebit = lines.reduce((s, l) => s + dollarsToCents(l.debit || "0"), 0);
  const totalCredit = lines.reduce((s, l) => s + dollarsToCents(l.credit || "0"), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  function updateLine(i: number, patch: Partial<JLine>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  const create = useMutation({
    mutationFn: () =>
      api.post("/journal-entries", {
        txnDate,
        memo: memo || undefined,
        isAdjusting,
        lines: lines
          .filter((l) => l.accountId && (l.debit || l.credit))
          .map((l) => ({
            accountId: l.accountId,
            debit: dollarsToCents(l.debit || "0"),
            credit: dollarsToCents(l.credit || "0"),
            description: l.description || undefined,
          })),
      }),
    onSuccess: onSaved,
  });

  return (
    <Modal open={open} onClose={onClose} title="New journal entry" wide>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid grid-cols-3 items-end gap-4">
          <Field required label="Date">
            <TextField
              type="date"
              value={txnDate}
              onChange={(e) => setTxnDate(e.target.value)}
            />
          </Field>
          <Field label="Memo">
            <TextField value={memo} onChange={(e) => setMemo(e.target.value)} />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={isAdjusting}
              onChange={(e) => setIsAdjusting(e.target.checked)}
            />
            Adjusting entry
          </label>
        </div>

        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="th">Account</th>
              <th className="th">Description</th>
              <th className="th w-32 text-right">Debit</th>
              <th className="th w-32 text-right">Credit</th>
              <th className="th w-8" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="py-2 pr-2">
                  <Select
                    value={l.accountId}
                    onChange={(e) => updateLine(i, { accountId: e.target.value })}
                  >
                    <option value="">— select —</option>
                    {(accounts ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="py-2 pr-2">
                  <TextField
                    value={l.description}
                    onChange={(e) =>
                      updateLine(i, { description: e.target.value })
                    }
                  />
                </td>
                <td className="py-2 pr-2">
                  <TextField
                    type="number"
                    step="0.01"
                    className="text-right"
                    value={l.debit}
                    onChange={(e) =>
                      updateLine(i, { debit: e.target.value, credit: "" })
                    }
                  />
                </td>
                <td className="py-2 pr-2">
                  <TextField
                    type="number"
                    step="0.01"
                    className="text-right"
                    value={l.credit}
                    onChange={(e) =>
                      updateLine(i, { credit: e.target.value, debit: "" })
                    }
                  />
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    className="text-gray-300 hover:text-red-500"
                    onClick={() =>
                      setLines((ls) => ls.filter((_, idx) => idx !== i))
                    }
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold">
              <td className="py-2" colSpan={2}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setLines((ls) => [...ls, { ...blank }])}
                >
                  + Add line
                </Button>
              </td>
              <td className="py-2 pr-2 text-right">
                <Money cents={totalDebit} currency={currency} />
              </td>
              <td className="py-2 pr-2 text-right">
                <Money cents={totalCredit} currency={currency} />
              </td>
              <td />
            </tr>
          </tfoot>
        </table>

        <div
          className={`rounded-md px-3 py-2 text-sm ${
            balanced
              ? "bg-brand-50 text-brand-700"
              : "bg-amber-50 text-amber-700"
          }`}
        >
          {balanced
            ? "✓ Entry is balanced."
            : `Out of balance by ${(
                Math.abs(totalDebit - totalCredit) / 100
              ).toFixed(2)} — debits must equal credits.`}
        </div>

        {create.error && <ErrorState error={create.error} />}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!balanced || create.isPending}>
            {create.isPending ? "Posting…" : "Post entry"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
